import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { updateWithdrawalStatus } from '../../redux/slices/withdrawalsSlice';
import { useTranslation } from '../../contexts/LanguageContext';

const WithdrawalStatusModal = ({ isOpen, onClose, withdrawal, onStatusUpdateSuccess }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [status, setStatus] = useState('');
  const [remarks, setRemarks] = useState('');
  const [receipt, setReceipt] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (withdrawal) {
      setStatus(withdrawal.status || '');
      setRemarks(withdrawal.remarks || '');
      setReceipt(null); // Reset file input when withdrawal changes
    }
  }, [withdrawal]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type (PDF or image)
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
      if (allowedTypes.includes(file.type)) {
        setReceipt(file);
      } else {
        alert(t('withdrawals.statusModal.invalidFile'));
        e.target.value = ''; // Reset file input
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!withdrawal?._id) return;

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('status', status);
      formData.append('userId', withdrawal?.userId?._id || withdrawal?.userId || '');
      formData.append('remarks', remarks);
      if (receipt) {
        formData.append('receipt', receipt);
      }

      await dispatch(updateWithdrawalStatus({
        withdrawalId: withdrawal._id,
        formData
      })).unwrap();

      // Call the success callback to refresh the table
      if (onStatusUpdateSuccess) {
        onStatusUpdateSuccess();
      }

      onClose();
    } catch (error) {
      console.error('Error updating withdrawal status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStatus(withdrawal?.status || '');
    setRemarks(withdrawal?.remarks || '');
    setReceipt(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <h2 className="text-xl font-bold mb-4">{t('withdrawals.statusModal.title')}</h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('withdrawals.statusModal.userName')}
            </label>
            <input
              type="text"
              value={withdrawal?.userId?.name || 'N/A'}
              readOnly
              className="w-full border rounded p-2 text-sm bg-gray-100"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('withdrawals.statusModal.amount')}
            </label>
            <input
              type="text"
              value={`$${withdrawal?.amount || 0}`}
              readOnly
              className="w-full border rounded p-2 text-sm bg-gray-100"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('withdrawals.statusModal.statusRequired')}
            </label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full border rounded p-2 text-sm"
              required
            >
              <option value="">{t('withdrawals.statusModal.selectStatus')}</option>
              <option value="pending">{t('withdrawals.statusModal.statusPending')}</option>
              <option value="processing">{t('withdrawals.statusModal.statusProcessing')}</option>
              <option value="completed">{t('withdrawals.statusModal.statusCompleted')}</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('withdrawals.statusModal.receipt')}
            </label>
            <input
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.gif"
              onChange={handleFileChange}
              className="w-full border rounded p-2 text-sm"
            />
            <p className="text-xs text-gray-500 mt-1">
              {t('withdrawals.statusModal.fileHelp')}
            </p>
            {receipt && (
              <p className="text-xs text-green-600 mt-1">
                {t('withdrawals.statusModal.selectedFile')} {receipt.name}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('withdrawals.statusModal.remarks')}
            </label>
            <textarea
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              className="w-full border rounded p-2 text-sm h-20 resize-none"
              placeholder={t('withdrawals.statusModal.remarksPlaceholder')}
            />
          </div>

          <div className="flex gap-2 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
              disabled={loading}
            >
              {t('withdrawals.statusModal.cancel')}
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              disabled={loading}
            >
              {loading ? t('withdrawals.statusModal.updating') : t('withdrawals.statusModal.update')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default WithdrawalStatusModal;
